import { ReactNode, useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import {
  primaryText,
  largeTextSize,
  semiBoldText,
} from '../../styling/stylingHelper';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ExpandModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Tailwind max-width class for the inner card. Default 'max-w-3xl'. */
  maxWidth?: string;
}

/**
 * Centered overlay modal for the "see all / maximize" affordance. Read-only
 * bigger scrollable view — no further drill-down. Closes on overlay click and
 * Escape. Fades + scales in smoothly on open.
 */
export const ExpandModal = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-3xl',
}: ExpandModalProps) => {
  const { t } = useTranslation();
  // Drives the entrance transition: starts hidden, flips visible one frame after mount.
  const [visible, setVisible] = useState(false);

  // Close on Escape + lock background scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const raf = requestAnimationFrame(() => setVisible(true));

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(raf);
      setVisible(false);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50 transition-opacity duration-200 ease-out',
        visible ? 'opacity-100' : 'opacity-0'
      )}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={cn(
          'bg-background dark:bg-background-dark rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto',
          'transition-all duration-200 ease-out',
          visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2',
          maxWidth
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-6 pt-6 pb-4 bg-background dark:bg-background-dark">
          <h2 className={cn(primaryText, largeTextSize, semiBoldText)}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex-none p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition"
            aria-label={t('common.close')}
          >
            <X className={cn('w-5 h-5', primaryText)} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6">{children}</div>
      </div>
    </div>
  );
};
